import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

import { getBackendUrl } from '../utils/getBackendUrl';
import { todayET } from '../utils/timezone';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const BargeLogForm = ({ serviceId, onSuccess, existingLog = null }) => {
  const [formData, setFormData] = useState(existingLog || {
    date: todayET(),
    barge_type: 'S', // S or L
    location: '',
    client: '',
    fuel_added: '',
    ramp_used: '',
    start_time: '07:00',
    finish_time: '17:00',
    operating_time: '',
    standby_time: '',
    platform_time: '',
    equipment_skidsteer_hrs: '',
    equipment_excavator_hrs: '',
    equipment_other_hrs: '',
    equipment_other_name: '',
    load_description: '',
    persons_on_barge: '',
    comments: ''
  });

  const calculateOperatingTime = () => {
    if (!formData.start_time || !formData.finish_time) return '';
    
    const start = new Date(`2000-01-01T${formData.start_time}`);
    const end = new Date(`2000-01-01T${formData.finish_time}`);
    
    if (end <= start) return '';
    
    const diffMs = end - start;
    const hours = diffMs / (1000 * 60 * 60);
    
    return hours.toFixed(1);
  };

  React.useEffect(() => {
    const calculated = calculateOperatingTime();
    if (calculated && calculated !== formData.operating_time) {
      setFormData(prev => ({ ...prev, operating_time: calculated }));
    }
  }, [formData.start_time, formData.finish_time]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        service_id: serviceId,
        ...formData
      };

      if (existingLog) {
        await axios.put(`${API}/barge-logs/${existingLog.id}`, payload);
        toast.success('Barge log updated successfully!');
      } else {
        await axios.post(`${API}/barge-logs`, payload);
        toast.success('Barge log created successfully!');
      }
      
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save barge log');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg">
      <h2 className="text-2xl font-bold text-gray-900 border-b-2 pb-2">
        Daily Barge Log
      </h2>

      {/* Date and Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="font-bold text-gray-900">Date</Label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
            className="border-2 border-gray-400"
            required
          />
        </div>
        
        <div>
          <Label className="font-bold text-gray-900">Barge Type</Label>
          <Select
            value={formData.barge_type}
            onValueChange={(value) => setFormData(prev => ({ ...prev, barge_type: value }))}
          >
            <SelectTrigger className="border-2 border-gray-400">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="S">S</SelectItem>
              <SelectItem value="L">L</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="font-bold text-gray-900">Location</Label>
          <Input
            value={formData.location}
            onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
            className="border-2 border-gray-400"
            placeholder="e.g., NARROWS"
            required
          />
        </div>
      </div>

      {/* Client and Resources */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="font-bold text-gray-900">Client</Label>
          <Input
            value={formData.client}
            onChange={(e) => setFormData(prev => ({ ...prev, client: e.target.value }))}
            className="border-2 border-gray-400"
            placeholder="Client name"
            required
          />
        </div>

        <div>
          <Label className="font-bold text-gray-900">Fuel Added</Label>
          <Input
            value={formData.fuel_added}
            onChange={(e) => setFormData(prev => ({ ...prev, fuel_added: e.target.value }))}
            className="border-2 border-gray-400"
            placeholder="Gallons/Liters"
          />
        </div>

        <div>
          <Label className="font-bold text-gray-900">Ramp Used #</Label>
          <Input
            value={formData.ramp_used}
            onChange={(e) => setFormData(prev => ({ ...prev, ramp_used: e.target.value }))}
            className="border-2 border-gray-400"
            placeholder="Ramp identifier"
          />
        </div>
      </div>

      {/* Time Tracking */}
      <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
        <h3 className="font-bold text-gray-900 mb-3">Time Tracking</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <Label className="font-bold text-gray-900">Start Time</Label>
            <Input
              type="time"
              value={formData.start_time}
              onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
              className="border-2 border-gray-400"
              required
            />
          </div>

          <div>
            <Label className="font-bold text-gray-900">Finish Time</Label>
            <Input
              type="time"
              value={formData.finish_time}
              onChange={(e) => setFormData(prev => ({ ...prev, finish_time: e.target.value }))}
              className="border-2 border-gray-400"
              required
            />
          </div>

          <div>
            <Label className="font-bold text-gray-900">Operating Time (hrs)</Label>
            <Input
              type="number"
              step="0.1"
              value={formData.operating_time}
              onChange={(e) => setFormData(prev => ({ ...prev, operating_time: e.target.value }))}
              className="border-2 border-gray-400 bg-white"
              placeholder="Auto-calculated"
            />
          </div>

          <div>
            <Label className="font-bold text-gray-900">Standby Time (hrs)</Label>
            <Input
              type="number"
              step="0.1"
              value={formData.standby_time}
              onChange={(e) => setFormData(prev => ({ ...prev, standby_time: e.target.value }))}
              className="border-2 border-gray-400"
              placeholder="0.0"
            />
          </div>

          <div>
            <Label className="font-bold text-gray-900">Platform Time (hrs)</Label>
            <Input
              type="number"
              step="0.1"
              value={formData.platform_time}
              onChange={(e) => setFormData(prev => ({ ...prev, platform_time: e.target.value }))}
              className="border-2 border-gray-400"
              placeholder="0.0"
            />
          </div>
        </div>
      </div>

      {/* Equipment Used */}
      <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
        <h3 className="font-bold text-gray-900 mb-3">Equipment Used</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="font-bold text-gray-900">Skidsteer (hrs)</Label>
            <Input
              type="number"
              step="0.1"
              value={formData.equipment_skidsteer_hrs}
              onChange={(e) => setFormData(prev => ({ ...prev, equipment_skidsteer_hrs: e.target.value }))}
              className="border-2 border-gray-400"
              placeholder="0.0"
            />
          </div>

          <div>
            <Label className="font-bold text-gray-900">Excavator (hrs)</Label>
            <Input
              type="number"
              step="0.1"
              value={formData.equipment_excavator_hrs}
              onChange={(e) => setFormData(prev => ({ ...prev, equipment_excavator_hrs: e.target.value }))}
              className="border-2 border-gray-400"
              placeholder="0.0"
            />
          </div>

          <div>
            <Label className="font-bold text-gray-900">Other Equipment</Label>
            <Input
              value={formData.equipment_other_name}
              onChange={(e) => setFormData(prev => ({ ...prev, equipment_other_name: e.target.value }))}
              className="border-2 border-gray-400"
              placeholder="Equipment name"
            />
          </div>

          <div>
            <Label className="font-bold text-gray-900">Other (hrs)</Label>
            <Input
              type="number"
              step="0.1"
              value={formData.equipment_other_hrs}
              onChange={(e) => setFormData(prev => ({ ...prev, equipment_other_hrs: e.target.value }))}
              className="border-2 border-gray-400"
              placeholder="0.0"
            />
          </div>
        </div>
      </div>

      {/* Load and Personnel */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="font-bold text-gray-900">Load Description</Label>
          <Input
            value={formData.load_description}
            onChange={(e) => setFormData(prev => ({ ...prev, load_description: e.target.value }))}
            className="border-2 border-gray-400"
            placeholder="e.g., SKID STEER, SIDING"
            required
          />
        </div>

        <div>
          <Label className="font-bold text-gray-900">Persons on Barge</Label>
          <Input
            value={formData.persons_on_barge}
            onChange={(e) => setFormData(prev => ({ ...prev, persons_on_barge: e.target.value }))}
            className="border-2 border-gray-400"
            placeholder="e.g., ZACH, WALT"
            required
          />
        </div>
      </div>

      {/* Comments */}
      <div>
        <Label className="font-bold text-gray-900">Comments</Label>
        <Textarea
          value={formData.comments}
          onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
          className="border-2 border-gray-400"
          placeholder="Additional notes, observations, or incidents..."
          rows={4}
        />
      </div>

      {/* Submit Button */}
      <div className="flex justify-end gap-3 pt-4 border-t-2">
        <Button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8"
        >
          {existingLog ? 'Update Log' : 'Save Barge Log'}
        </Button>
      </div>
    </form>
  );
};

export default BargeLogForm;
